import { NightComponent, RateCard, RateSlab } from '../../services/rateCardService';

/**
 * Text-input model for the Rate Card editor. Each slab's "from km" is derived from the previous
 * slab's "to km", so the table can't express a gap; the server still validates everything.
 */

export interface SlabForm { toKm: string; perKm: string }

export interface CardForm {
  baseFare: string;
  slabs: SlabForm[];
  pickupCharge: string;
  driverAdditions: string;
  congestionCharges: string;
  minimumFare: string;
  timePerMin: string;
  waitFreeMin: string;
  waitPerMin: string;
  waitCap: string;
  lpFreeKm: string;
  lpPerKm: string;
  lpCap: string;
  nightMode: 'none' | 'percent' | 'rates';
  nightPct: string;
  nightAppliesTo: NightComponent[];
  nightBaseFare: string;
  nightSlabsOn: boolean;
  nightSlabs: SlabForm[];
}

export const NIGHT_COMPONENT_LABELS: Record<NightComponent, string> = {
  base: 'Base fare', distance: 'Distance fare', time: 'Time fare', pickup: 'Pickup charge', wait: 'Wait time', longPickup: 'Long pickup',
};

const str = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

const slabsToForm = (slabs: RateSlab[]): SlabForm[] => slabs.map((s) => ({ toKm: str(s.toKm), perKm: str(s.perKm) }));

/** "" → NaN for required numbers, so the server rejects it with a clear message instead of a silent 0. */
const req = (s: string) => (s.trim() === '' ? NaN : Number(s));
const opt = (s: string) => (s.trim() === '' ? null : Number(s));

/** fromKm of slab i = toKm of slab i-1 (0 for the first). */
export const fromKmOf = (slabs: SlabForm[], i: number): number => (i === 0 ? 0 : req(slabs[i - 1].toKm));

export const formToSlabs = (slabs: SlabForm[]): RateSlab[] => slabs.map((s, i) => ({
  fromKm: fromKmOf(slabs, i),
  toKm: i === slabs.length - 1 ? null : req(s.toKm),
  perKm: req(s.perKm),
}));

export const cardToForm = (c: RateCard): CardForm => ({
  baseFare: str(c.baseFare),
  slabs: slabsToForm(c.slabs),
  pickupCharge: str(c.pickupCharge),
  driverAdditions: str(c.driverAdditions),
  congestionCharges: str(c.congestionCharges),
  minimumFare: str(c.minimumFare),
  timePerMin: str(c.time?.perMin ?? 0),
  waitFreeMin: str(c.wait.freeMin),
  waitPerMin: str(c.wait.perMin),
  waitCap: str(c.wait.capAmount),
  lpFreeKm: str(c.longPickup.freeKm),
  lpPerKm: str(c.longPickup.perKm),
  lpCap: str(c.longPickup.capAmount),
  nightMode: c.night.mode,
  nightPct: c.night.mode === 'percent' ? str(c.night.pct) : '20',
  nightAppliesTo: c.night.mode === 'percent' ? [...c.night.appliesTo] : ['base', 'distance'],
  nightBaseFare: c.night.mode === 'rates' ? str(c.night.baseFare) : '',
  nightSlabsOn: c.night.mode === 'rates' && c.night.slabs !== null,
  nightSlabs: c.night.mode === 'rates' && c.night.slabs ? slabsToForm(c.night.slabs) : slabsToForm(c.slabs),
});

export const formToCard = (f: CardForm): RateCard => ({
  schemaVersion: 1,
  baseFare: req(f.baseFare),
  slabs: formToSlabs(f.slabs),
  pickupCharge: req(f.pickupCharge),
  driverAdditions: req(f.driverAdditions),
  congestionCharges: req(f.congestionCharges),
  minimumFare: opt(f.minimumFare),
  wait: { freeMin: req(f.waitFreeMin), perMin: req(f.waitPerMin), capAmount: opt(f.waitCap) },
  longPickup: { freeKm: req(f.lpFreeKm), perKm: req(f.lpPerKm), capAmount: opt(f.lpCap) },
  time: { perMin: req(f.timePerMin) },
  night: f.nightMode === 'percent'
    ? { mode: 'percent', pct: req(f.nightPct), appliesTo: [...f.nightAppliesTo] }
    : f.nightMode === 'rates'
      ? { mode: 'rates', baseFare: opt(f.nightBaseFare), slabs: f.nightSlabsOn ? formToSlabs(f.nightSlabs) : null }
      : { mode: 'none' },
});

// ── Slab table operations (immutable) ──────────────────────────────────────

/** Adds a slab by splitting the open last one: it now ends `step` km after it starts. */
export const addSlab = (slabs: SlabForm[], step = 5): SlabForm[] => {
  const last = slabs[slabs.length - 1];
  const from = fromKmOf(slabs, slabs.length - 1);
  const end = Number.isFinite(from) ? from + step : step;
  return [...slabs.slice(0, -1), { ...last, toKm: String(end) }, { toKm: '', perKm: last.perKm }];
};

/** Removes slab i; its neighbour absorbs the range so the table stays contiguous. The only slab stays. */
export const removeSlab = (slabs: SlabForm[], i: number): SlabForm[] => {
  if (slabs.length <= 1 || i < 0 || i >= slabs.length) return slabs;
  const out = slabs.map((s) => ({ ...s }));
  if (i === slabs.length - 1) out[i - 1].toKm = '';
  else if (i > 0) out[i - 1].toKm = slabs[i].toKm;
  out.splice(i, 1);
  return out;
};

export const updateSlab = (slabs: SlabForm[], i: number, patch: Partial<SlabForm>): SlabForm[] =>
  slabs.map((s, j) => (j === i ? { ...s, ...patch } : s));

export const slabLabel = (fromKm: number, toKm: number | null) =>
  toKm === null ? `${fromKm} km and above` : `${fromKm}–${toKm} km`;

/** True when the draft differs from the saved card (compares the wire shape, not the text). */
export const isDirty = (form: CardForm, saved: RateCard | null) =>
  !saved || JSON.stringify(formToCard(form)) !== JSON.stringify(formToCard(cardToForm(saved)));
